// SB6-ANA-FILT-06 (Track D / D9) — per-user filter-config client hooks.
//
// The two L3 advanced widgets (AI Filter Module, Defect Filter Widget)
// persist their configuration server-side via the lyse/v1@7.2.0
// `/me/filter-configs` CRUD (per-user, JWT-resolved — never a param). Two
// flavours of config share the same table, distinguished by `name`:
//
//   * **named presets** (`name: "Crack focus"`) — explicit save/load, listed
//     in the widget's presets control; and
//   * the implicit **last-used** row (`name: null`) — one per
//     `(kind, modelSignature)` that the FE upserts on every committed change
//     and auto-loads on DA open (validity-gated by `modelSignature` — D9).
//
// A config is only listable/loadable against a matching `modelSignature`
// (the BE filters by the query param; the FE just passes the right
// signature). This is the cross-setup guard: a preset built under one
// model setup cannot load under a different one.
//
// All hooks invalidate the matching list query on settle so the presets
// dropdown stays in lock-step with the server.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import type {
  FilterConfig,
  FilterConfigKind,
  ListFilterConfigsResponse,
  SaveFilterConfigBody,
  UpdateFilterConfigBody,
} from "@aeriosiq/types";

/**
 * The implicit "last-used" config carries `name: null`; named presets
 * carry a non-null label. A single sentinel makes the intent explicit at
 * call sites (`name: LAST_USED_CONFIG_NAME`).
 */
export const LAST_USED_CONFIG_NAME = null;

export interface FilterConfigScope {
  /** Which advanced widget the config belongs to. */
  kind: FilterConfigKind;
  /**
   * The model-setup signature (AI: `AiClassFacetsResponse.modelSignature`;
   * defect: the DA's defect-model+class-model signature). A config is only
   * listable/loadable against a matching signature (D9).
   */
  modelSignature: string;
}

export const filterConfigKeys = {
  all: ["filter-configs"] as const,
  list: (scope: FilterConfigScope) =>
    [
      ...filterConfigKeys.all,
      "list",
      scope.kind,
      scope.modelSignature,
    ] as const,
};

/**
 * List the caller's saved configs for a `(kind, modelSignature)`. Includes
 * BOTH named presets and the implicit last-used row (`name: null`); the
 * consumer separates them by `name`. `enabled` is gated on a non-empty
 * signature (the widget passes `""` until the facets response settles).
 */
export function useFilterConfigsQuery(
  scope: FilterConfigScope,
  opts?: { enabled?: boolean },
): UseQueryResult<FilterConfig[]> {
  const api = useApi();
  const enabled = (opts?.enabled ?? true) && scope.modelSignature.length > 0;
  return useQuery<FilterConfig[]>({
    queryKey: filterConfigKeys.list(scope),
    enabled,
    // Presets change rarely; keep them fresh enough that a save in another
    // tab surfaces within the cache window without hammering the endpoint.
    staleTime: 30_000,
    queryFn: async () => {
      const response = await api.get<ListFilterConfigsResponse>(
        "/me/filter-configs",
        {
          params: {
            kind: scope.kind,
            modelSignature: scope.modelSignature,
          },
        },
      );
      return response.data.items;
    },
  });
}

/**
 * Split a list of configs into the named presets (sorted by name) and the
 * implicit last-used row (`name: null`), so the UI can show the dropdown
 * and apply the auto-restore separately.
 */
export function partitionConfigs(configs: readonly FilterConfig[]): {
  presets: FilterConfig[];
  lastUsed: FilterConfig | null;
} {
  const presets: FilterConfig[] = [];
  let lastUsed: FilterConfig | null = null;
  for (const c of configs) {
    if (c.name === null) lastUsed = c;
    else presets.push(c);
  }
  presets.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  return { presets, lastUsed };
}

/**
 * Create a named preset, or upsert the implicit last-used config
 * (`name: null`) for `(kind, modelSignature)`. The dev-api / BE upserts
 * the last-used row on conflict, so this doubles as the last-used writer.
 * Invalidates the matching list on settle.
 */
export function useCreateFilterConfigMutation(scope: FilterConfigScope) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<FilterConfig, unknown, SaveFilterConfigBody>({
    mutationFn: async (body) => {
      const response = await api.post<FilterConfig>("/me/filter-configs", body);
      return response.data;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: filterConfigKeys.list(scope) });
    },
  });
}

/** Rename and/or replace an existing config's blob. */
export function useUpdateFilterConfigMutation(scope: FilterConfigScope) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<
    FilterConfig,
    unknown,
    { configId: string; body: UpdateFilterConfigBody }
  >({
    mutationFn: async ({ configId, body }) => {
      const response = await api.patch<FilterConfig>(
        `/me/filter-configs/${encodeURIComponent(configId)}`,
        body,
      );
      return response.data;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: filterConfigKeys.list(scope) });
    },
  });
}

/** Delete one of the caller's configs (named or last-used). */
export function useDeleteFilterConfigMutation(scope: FilterConfigScope) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<unknown, unknown, string>({
    mutationFn: async (configId) => {
      await api.delete(`/me/filter-configs/${encodeURIComponent(configId)}`);
      return undefined;
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: filterConfigKeys.list(scope) });
    },
  });
}
